import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [dashboardStats, setDashboardStats] = useState({
    accounts: { total: 0, active: 0 },
    domains: { total: 0, super_admin_managed: 0 },
    emails: { total_logs: 0, sent_30_days: 0 },
    recent_activity: []
  });
  const [loading, setLoading] = useState(true);
  
  // Account tracking state
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(''); // NEW: Domain selection
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountMetrics, setAccountMetrics] = useState({
    emails_sent: 0,
    delivery_rate: 0,
    spam_complaints: 0,
    spam_rate: 0,
    replies_received: 0,
    emails_opened: 0,
    open_rate: 0,
    daily_breakdown: []
  });
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Daily Schedule state
  const [dailySchedule, setDailySchedule] = useState({
    today_cycles: 0,
    max_cycles: null, // Will be loaded from API
    next_cycle_time: "MongoDB System Active",
    office_hours: "10:00 - 23:00",
    auto_enabled: true,
    schedule: []
  });
  const [nextWarmup, setNextWarmup] = useState({
    time: "Ready",
    status: "ready",
    auto_enabled: true,
    cycle_number: 1
  });
  
  // Current Week state
  const [currentWeek, setCurrentWeek] = useState({
    week_number: null,
    week_start_date: null,
    next_week_date: null,
    user_selected: false
  });

  // Domain warmup tracking state
  const [domainHealth, setDomainHealth] = useState([]);
  const [loadingDomainHealth, setLoadingDomainHealth] = useState(false);
  const [selectedDomainFilter, setSelectedDomainFilter] = useState('all'); // Domain filter

  useEffect(() => {
    fetchDashboardStats();
    fetchEmailAccounts();
    fetchDailySchedule();
    fetchNextWarmup();
    fetchCurrentWeek();
    // Don't auto-fetch domain health - let user trigger it
    
    // Auto-refresh dashboard stats every 5 minutes (reduced frequency to prevent constant refreshing)
    const interval = setInterval(fetchDashboardStats, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Auto-fetch metrics when account changes
  useEffect(() => {
    if (selectedAccount) {
      fetchAccountMetrics(selectedAccount);
    }
  }, [selectedAccount]);

  // Auto-refresh account metrics every 10 minutes when an account is selected (reduced frequency)
  useEffect(() => {
    if (selectedAccount) {
      const metricsInterval = setInterval(() => {
        fetchAccountMetrics(selectedAccount);
      }, 600000); // 10 minutes
      
      return () => clearInterval(metricsInterval);
    }
  }, [selectedAccount]);

  const fetchDashboardStats = async () => {
    try {
      // Add timeout protection
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000); // 12 second max
      
      // Use MongoDB dashboard API directly
      const response = await fetch('/api/v1/dashboard');
      const data = await response.json();
      clearTimeout(timeoutId);
      
      if (data && data.success) {
        // Handle MongoDB API response structure
        const dashboardData = data.data;
        
        // Ensure all required properties exist with defaults
        const processedData = {
          accounts: dashboardData.accounts || { total: 0, active: 0 },
          domains: dashboardData.domains || { total: 0, super_admin_managed: 0 },
          super_admins: dashboardData.super_admins || { total: 0, verified: 0 },
          emails: dashboardData.emails || { total_logs: 0, sent_30_days: 0 },
          recent_activity: dashboardData.recent_activity || [],
          recipients: { total: 0, active: 0 }, // Default for MongoDB
          campaigns: { active: 0, completed: 0 }, // Default for MongoDB
          warmup: dashboardData.warmup || { cycles_today: 0, max_cycles: null } // Use API data only
        };
        
        console.log('✅ MongoDB Dashboard data processed:', processedData);
        console.log('🔍 Warmup data from API:', processedData.warmup);
        setDashboardStats(processedData);
        
        // Also update dailySchedule if warmup data is available
        if (processedData.warmup) {
          console.log('🔄 Updating dailySchedule with max_cycles:', processedData.warmup.max_cycles);
          setDailySchedule(prev => ({
            ...prev,
            today_cycles: processedData.warmup.cycles_today || prev.today_cycles,
            max_cycles: processedData.warmup.max_cycles !== undefined ? processedData.warmup.max_cycles : prev.max_cycles
          }));
        }
      } else {
        console.error('Invalid dashboard response:', response);
        toast.error('Invalid dashboard data received');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      
      if (error.name === 'AbortError') {
        toast.error('Dashboard request timed out - please refresh');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - check backend connection');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailAccounts = async () => {
    setLoadingAccounts(true);
    try {
      // Use MongoDB dashboard API to get accounts
      const response = await fetch('/api/v1/dashboard');
      const data = await response.json();
      
      console.log('MongoDB accounts response:', data); // Debug log
      
      if (data && data.success && data.data) {
        // Get actual user accounts from MongoDB
        const usersResponse = await fetch('/api/v1/monitoring/users');
        const usersData = await usersResponse.json();
        
        if (usersData && usersData.success && usersData.data && usersData.data.users) {
          const accounts = usersData.data.users.map(user => ({
            email: user.email,
            domain: user.domain,
            isp: user.isp,
            is_active: user.is_active,
            source: "mongodb_atlas"
          }));
          
          console.log('✅ MongoDB accounts loaded:', accounts.length);
          setEmailAccounts(accounts);
          
          // Don't auto-select - let user choose domain first
        } else {
          console.log('No users data found, using fallback');
          setEmailAccounts([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch email accounts:', error);
      toast.error('Failed to load email accounts from MongoDB');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchDailySchedule = async () => {
    try {
      // For MongoDB-only system, get schedule data from API
      const response = await fetch('/api/v1/dashboard');
      const data = await response.json();
      
      if (data && data.success && data.data && data.data.warmup) {
        setDailySchedule({
          today_cycles: data.data.warmup.cycles_today || 0,
          max_cycles: data.data.warmup.max_cycles, // Use exact API value
          next_cycle_time: "MongoDB System Active",
          office_hours: "10:00 - 23:00",
          auto_enabled: true
        });
      } else {
        // Keep existing values if API doesn't have warmup data
        console.warn('No warmup data from API, keeping current values');
      }
      
    } catch (error) {
      console.error('Error setting daily schedule:', error);
    }
  };

  const fetchNextWarmup = async () => {
    try {
      // Get exact next warmup time from backend
      const response = await fetch('/api/v1/system/next-warmup');
      const data = await response.json();
      
      if (data && data.success && data.data) {
        const warmupData = data.data;
        
        setNextWarmup({
          time: warmupData.next_warmup_time,
          status: warmupData.status,
          auto_enabled: warmupData.auto_enabled,
          cycle_number: 1,
          minutes_until: warmupData.minutes_until || 0,
          next_cycle_time: warmupData.next_warmup_time
        });
        
        console.log('✅ Next warmup time loaded from backend:', warmupData);
      } else {
        // Fallback if API fails
        setNextWarmup({
          time: "API Error",
          status: "error",
          auto_enabled: false,
          cycle_number: 1,
          minutes_until: 0
        });
      }
    } catch (error) {
      console.error('Error fetching next warmup:', error);
      // Fallback on error
      setNextWarmup({
        time: "Loading...",
        status: "loading",
        auto_enabled: true,
        cycle_number: 1,
        minutes_until: 0
      });
    }
  };

  const fetchCurrentWeek = async () => {
    try {
      const response = await fetch('/api/v1/weekly-templates/current-week');
      const data = await response.json();
      
      if (data && data.success && data.data) {
        const weekData = data.data;
        const currentWeekNum = weekData.current_week;
        const weekStartDate = new Date(weekData.week_start_date);
        
        // Calculate next week transition (7 days after week start)
        const nextWeekDate = new Date(weekStartDate);
        nextWeekDate.setDate(nextWeekDate.getDate() + 7);
        
        setCurrentWeek({
          week_number: currentWeekNum,
          week_start_date: weekStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          next_week_date: nextWeekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          user_selected: weekData.user_selected || false
        });
        
        console.log('✅ Current week loaded:', currentWeekNum);
      }
    } catch (error) {
      console.error('Error fetching current week:', error);
      setCurrentWeek({
        week_number: 4,
        week_start_date: 'Loading...',
        next_week_date: 'Loading...',
        user_selected: false
      });
    }
  };

  const fetchAccountMetrics = async (accountEmail) => {
    if (!accountEmail) return;
    
    setLoadingMetrics(true);
    try {
      // Use MongoDB user metrics endpoint directly with aggressive cache-busting
      const timestamp = new Date().getTime();
      const randomId = Math.random().toString(36).substring(7);
      const response = await fetch(`/api/v1/monitoring/users/${encodeURIComponent(accountEmail)}?_t=${timestamp}&_r=${randomId}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();
      
      // Reduced debug logging to prevent console spam
      console.log('📊 Account metrics API response for:', accountEmail);
      
      if (data && data.success && data.data) {
        const apiData = data.data;
        console.log('✅ Fresh data received:', {
          emails_sent: apiData.emails_sent,
          replies_received: apiData.replies_received,
          last_activity: apiData.last_activity
        });
        
        const metricsData = {
          emails_sent: apiData.emails_sent || 0,
          delivery_rate: apiData.delivery_rate || 0,
          spam_complaints: apiData.spam_complaints || 0,
          spam_rate: apiData.spam_rate || 0,
          replies_received: apiData.replies_received || 0,
          emails_opened: apiData.emails_opened || 0,
          open_rate: apiData.open_rate || 0,
          reply_rate: apiData.reply_rate || 0,
          daily_breakdown: [],
          last_activity: apiData.last_activity || null
        };
        
        setAccountMetrics(metricsData);
      } else {
        console.log('❌ API response invalid, using fallback metrics');
        console.log('Data object:', data);
        // Set safe fallback metrics
        setAccountMetrics({
          emails_sent: 0,
          delivery_rate: 0,
          spam_complaints: 0,
          spam_rate: 0,
          replies_received: 0,
          emails_opened: 0,
          open_rate: 0,
          reply_rate: 0,
          daily_breakdown: [],
          last_activity: null
        });
      }
    } catch (error) {
      console.error('Failed to fetch account metrics:', error);
      toast.error('Failed to load account metrics');
      setAccountMetrics(null);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchDomainHealth = async (targetDomain = null) => {
    setLoadingDomainHealth(true);
    try {
      const response = await fetch('/api/v1/monitoring/users');
      const data = await response.json();
      
      if (data && data.success && data.data && data.data.users) {
        const accounts = data.data.users;
        
        // Group accounts by domain
        const domainGroups = {};
        for (const account of accounts) {
          const domain = account.domain;
          
          // If targetDomain is specified, only process that domain
          if (targetDomain && domain !== targetDomain) {
            continue;
      }
          
          if (!domainGroups[domain]) {
            domainGroups[domain] = {
              domain: domain,
              accounts: [],
              totalEmails: 0,
              totalDelivered: 0,
              totalSpam: 0,
              totalReplies: 0
            };
          }
          domainGroups[domain].accounts.push(account);
        }
        
        // Fetch metrics for each domain
        const domainHealthData = [];
        for (const [domain, group] of Object.entries(domainGroups)) {
          let domainTotalEmails = 0;
          let domainSuccessfulEmails = 0; // Track ONLY successful emails
          let totalDeliveryRate = 0;
          let totalSpamRate = 0;
          let totalReplyRate = 0;
          let accountsWithData = 0;
          
          // Aggregate metrics from all accounts in this domain
          for (const account of group.accounts) {
    try {
              const metricsResponse = await fetch(`/api/v1/monitoring/users/${encodeURIComponent(account.email)}`);
              const metricsData = await metricsResponse.json();
              
              if (metricsData && metricsData.success && metricsData.data) {
                const metrics = metricsData.data;
                const emailsSent = metrics.emails_sent || 0;
                const accountDeliveryRate = metrics.delivery_rate || 0;
                
                if (emailsSent > 0) {
                  domainTotalEmails += emailsSent;
                  
                  // CRITICAL FIX: Only count successfully delivered emails
                  // If 100 emails sent but all failed, this will be 0, not 100
                  const successfulEmails = Math.round(emailsSent * (accountDeliveryRate / 100));
                  domainSuccessfulEmails += successfulEmails;
                  
                  // Use percentage rates from API (they're already calculated correctly)
                  totalDeliveryRate += accountDeliveryRate;
                  totalSpamRate += metrics.spam_rate || 0;
                  totalReplyRate += metrics.reply_rate || 0;
                  accountsWithData++;
                }
      }
            } catch (err) {
              console.error(`Failed to fetch metrics for ${account.email}:`, err);
            }
          }
          
          // Calculate average rates across all accounts in domain
          const deliveryRate = accountsWithData > 0 ? totalDeliveryRate / accountsWithData : 0;
          const spamRate = accountsWithData > 0 ? totalSpamRate / accountsWithData : 0;
          const replyRate = accountsWithData > 0 ? totalReplyRate / accountsWithData : 0;
          
          // Calculate expected emails based on number of accounts
          // Each account should send 300 successful emails for full warmup
          const emailsPerAccountTarget = 300;
          const totalAccountsInDomain = group.accounts.length;
          const targetSuccessfulEmails = totalAccountsInDomain * emailsPerAccountTarget;
          
          console.log(`📊 ${domain}: Accounts=${totalAccountsInDomain}, Total=${domainTotalEmails}, Successful=${domainSuccessfulEmails}/${targetSuccessfulEmails}, Delivery=${deliveryRate.toFixed(1)}%, Spam=${spamRate.toFixed(1)}%, Replies=${replyRate.toFixed(1)}%`);
          
          // Warmup score calculation (SCALED by account count):
          // - Successful emails sent (40%): More successful emails = better warmup
          //   NOTE: If emails are sent but all fail (0% delivery), this will be 0 points, not 40
          //   This ensures we don't reward volume without success
          //   Benchmark: 300 successful emails PER ACCOUNT = 40 points (max)
          //   Examples:
          //     - 1 account domain: 300 emails = 40 points
          //     - 4 account domain: 1200 emails (4 × 300) = 40 points
          //     - 10 account domain: 3000 emails (10 × 300) = 40 points
          // - Delivery rate (30%): Higher delivery = better reputation
          // - Low spam rate (20%): Lower spam = better reputation
          // - Reply engagement (10%): More replies = better engagement
          const emailScore = Math.min((domainSuccessfulEmails / targetSuccessfulEmails) * 40, 40); // Max 40 points
          const deliveryScore = (deliveryRate / 100) * 30; // Max 30 points
          const spamScore = Math.max(20 - (spamRate * 2), 0); // Max 20 points (penalty for spam)
          const replyScore = Math.min(replyRate * 2, 10); // Max 10 points
          
          const warmupScore = Math.round(emailScore + deliveryScore + spamScore + replyScore);
          
          // Determine status based on score
          let status, statusColor, statusText, recommendation;
          if (warmupScore >= 80) {
            status = 'ready';
            statusColor = 'green';
            statusText = '✅ Ready for Production';
            recommendation = 'Domain is fully warmed up and ready for marketing campaigns';
          } else if (warmupScore >= 50) {
            status = 'warming';
            statusColor = 'yellow';
            statusText = '🔥 Warming Up';
            recommendation = 'Continue warmup cycles. Increase email volume gradually.';
          } else if (warmupScore >= 20) {
            status = 'early';
            statusColor = 'blue';
            statusText = '🌱 Early Stage';
            recommendation = 'New domain. Continue with low-volume warmup cycles.';
          } else {
            status = 'new';
            statusColor = 'gray';
            statusText = '🆕 Just Started';
            recommendation = 'Brand new domain. Start with plain text emails and build reputation slowly.';
          }
          
          domainHealthData.push({
            domain,
            accountCount: group.accounts.length,
            totalEmails: domainTotalEmails,
            successfulEmails: domainSuccessfulEmails,
            targetSuccessfulEmails: targetSuccessfulEmails,
            deliveryRate: Math.round(deliveryRate * 10) / 10,
            spamRate: Math.round(spamRate * 10) / 10,
            replyRate: Math.round(replyRate * 10) / 10,
            warmupScore,
            status,
            statusColor,
            statusText,
            recommendation
          });
        }
        
        // Sort by warmup score (highest first)
        domainHealthData.sort((a, b) => b.warmupScore - a.warmupScore);
        
        console.log('✅ Domain health data calculated:', domainHealthData);
        setDomainHealth(domainHealthData);
      }
    } catch (error) {
      console.error('Failed to fetch domain health:', error);
    } finally {
      setLoadingDomainHealth(false);
    }
  };

  const handleAccountChange = (accountEmail) => {
    setSelectedAccount(accountEmail);
    if (accountEmail) {
      fetchAccountMetrics(accountEmail);
    } else {
      setAccountMetrics(null);
    }
  };

  // NEW: Get unique domains from email accounts
  const getUniqueDomains = () => {
    const domains = emailAccounts
      .map(account => account.domain)
      .filter(domain => domain); // Remove empty domains
    return [...new Set(domains)].sort(); // Remove duplicates and sort
  };

  // NEW: Get emails filtered by selected domain
  const getFilteredEmails = () => {
    if (!selectedDomain) return [];
    return emailAccounts.filter(account => account.domain === selectedDomain);
  };

  // NEW: Handle domain selection
  const handleDomainChange = (domain) => {
    setSelectedDomain(domain);
    setSelectedAccount(''); // Reset selected account
    setAccountMetrics(null); // Clear metrics
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 mt-15">
        <div className="loading-spinner mr-2"></div>
        <span>Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 fade-in mt-20">
      {/* Page Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm sm:text-base">Email warmup system overview and quick actions</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        <div className="metric-card">
          <h3>Total Accounts</h3>
          <div className="flex items-center justify-between">
            <p>{dashboardStats?.accounts?.total || 0}</p>
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {dashboardStats?.accounts?.active} active
          </div>
        </div>

        <div className="metric-card">
          <h3>Recipients</h3>
          <div className="flex items-center justify-between">
            <p>{dashboardStats?.recipients?.total || 0}</p>
            <div className="bg-green-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {dashboardStats?.recipients?.active} active
          </div>
        </div>

        <div className="metric-card">
          <h3>Emails Sent (30 days)</h3>
          <div className="flex items-center justify-between">
            <p>{dashboardStats?.emails?.sent_30_days || 0}</p>
            <div className="bg-purple-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div className="text-xs text-green-500 mt-2">
            ↗️ Growing
          </div>
        </div>

        <div className="metric-card">
          <h3>Warmup Cycles Today</h3>
          <div className="flex items-center justify-between">
            <p>
              {dashboardStats?.warmup?.cycles_today || 0}/
              {dashboardStats?.warmup?.max_cycles !== null ? dashboardStats?.warmup?.max_cycles : '...'}
            </p>
            <div className="bg-orange-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Limit: {dashboardStats?.warmup?.max_cycles !== null ? `${dashboardStats?.warmup?.max_cycles} per day` : 'Loading...'}
          </div>
        </div>
      </div>

      {/* Daily Schedule Section */}
      <div className="bg-white rounded-lg p-6 card-shadow mb-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Daily Warmup Schedule
          </h3>
          <button 
            onClick={() => { fetchDailySchedule(); fetchNextWarmup(); fetchCurrentWeek(); }}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Next Warmup Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Next Warmup
            </h4>
            {nextWarmup ? (
              <div>
                <div className="text-2xl font-bold text-blue-900 mb-1">
                  {nextWarmup.time || nextWarmup.next_cycle_time || "16:30"}
                </div>
                <div className="text-sm text-blue-700">
                  In {nextWarmup.minutes_until || 30} minutes
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  Cycle #{nextWarmup.cycle_number || 1} • {nextWarmup.day_name || "Today"}
                </div>
              </div>
            ) : (
              <div className="text-blue-700">Loading next warmup...</div>
            )}
          </div>

          {/* Today's Schedule Card */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
            <h4 className="text-sm font-medium text-green-900 mb-3 flex items-center">
              <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v8a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Today's Schedule
            </h4>
            {dailySchedule ? (
              <div>
                <div className="text-lg font-semibold text-green-900 mb-2">
                  {dailySchedule.max_cycles !== null ? `${dailySchedule.max_cycles} cycles max` : 'Loading cycles...'}
                </div>
                <div className="text-sm text-green-700">
                  {dailySchedule.next_cycle_time || "MongoDB System Active"}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  Office: {dailySchedule.office_hours || "10:00 - 23:00"}
                </div>
                {currentWeek.week_number && (
                  <div className="mt-2 pt-2 border-t border-green-300">
                    <div className="text-xs font-medium text-green-800">
                      Current Week: <span className="font-bold">Week {currentWeek.week_number}</span>
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      Week {currentWeek.week_number + 1} starts: {currentWeek.next_week_date}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-green-700">Loading schedule...</div>
            )}
          </div>
        </div>

        {/* Schedule Timeline */}
        {dailySchedule && dailySchedule.schedule && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <svg className="w-4 h-4 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Fixed Times (Automatically Scheduled)
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(dailySchedule?.schedule || []).map((cycle, index) => {
                const statusColors = {
                  'completed': 'bg-green-100 border-green-300 text-green-800',
                  'starting': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                  'scheduled': 'bg-blue-100 border-blue-300 text-blue-800'
                };
                const statusIcons = {
                  'completed': '✅',
                  'starting': '🚀',
                  'scheduled': '⏰'
                };
                
                return (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg border-2 text-center ${statusColors[cycle.status] || 'bg-gray-100 border-gray-300 text-gray-800'}`}
                  >
                    <div className="text-lg font-semibold">
                      {cycle.scheduled_time}
                    </div>
                    <div className="text-xs">
                      {statusIcons[cycle.status]} Cycle {cycle.cycle_number}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Email Account Tracking */}
      <div className="bg-white rounded-lg p-4 sm:p-6 card-shadow">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">Email Account Tracking</h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Domain Selection Dropdown */}
            <select
              value={selectedDomain}
              onChange={(e) => handleDomainChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-auto"
              disabled={loadingAccounts}
            >
              <option value="">Select Domain</option>
              {getUniqueDomains().map((domain) => (
                <option key={domain} value={domain}>
                  {domain}
                </option>
              ))}
            </select>

            {/* Email Selection Dropdown (only show if domain is selected) */}
            {selectedDomain && (
            <select
              value={selectedAccount}
              onChange={(e) => handleAccountChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-auto"
              disabled={loadingAccounts}
            >
                <option value="">Select Email</option>
                {getFilteredEmails().map((account, index) => (
                <option key={account.email || index} value={account.email || `account-${index}`}>
                    {account.email || `Account ${index + 1}`}
                </option>
              ))}
            </select>
            )}
            
            {selectedAccount && (
              <button
                onClick={() => {
                  console.log('🔄 Manual refresh clicked for:', selectedAccount);
                  // Force clear the current metrics first
                  setAccountMetrics(null);
                  // Then fetch new data
                  fetchAccountMetrics(selectedAccount);
                }}
                disabled={loadingMetrics}
                className="btn-secondary text-sm px-3 py-2"
              >
                {loadingMetrics ? 'Refreshing...' : '🔄 Refresh'}
              </button>
            )}

          </div>
        </div>

        {loadingMetrics ? (
          <div className="flex items-center justify-center h-32">
            <div className="loading-spinner mr-2"></div>
            <span>Loading metrics...</span>
          </div>
        ) : selectedAccount && accountMetrics ? (
          <div className="space-y-6">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900">Emails Sent</p>
                    <p className="text-2xl font-bold text-blue-700">{accountMetrics.emails_sent}</p>
                  </div>
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0v-2a2 2 0 012-2h8a2 2 0 012 2v2" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  {accountMetrics.delivery_rate}% delivery rate
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-900">Spam Complaints</p>
                    <p className="text-2xl font-bold text-red-700">{accountMetrics.spam_complaints}</p>
                  </div>
                  <div className="bg-red-100 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.732 8.5C3.962 10.333 4.924 12 6.464 12z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-red-600 mt-1">
                  {accountMetrics.spam_rate}% spam rate
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Replies Received</p>
                    <p className="text-2xl font-bold text-yellow-700">{accountMetrics.replies_received}</p>
                  </div>
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-yellow-600 mt-1">
                  Engagement metric
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-900">Emails Opened</p>
                    <p className="text-2xl font-bold text-green-700">{accountMetrics.emails_opened}</p>
                  </div>
                  <div className="bg-green-100 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  {accountMetrics.open_rate}% open rate
                </p>
              </div>
            </div>

            {/* Daily Activity Chart */}
            {accountMetrics?.daily_breakdown && Array.isArray(accountMetrics.daily_breakdown) && accountMetrics.daily_breakdown.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Last 7 Days Activity</h4>
                <div className="grid grid-cols-7 gap-2">
                  {(accountMetrics?.daily_breakdown || []).map((day, index) => (
                    <div key={day.date || `day-${index}`} className="text-center">
                      <p className="text-xs text-gray-500 mb-2">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </p>
                      <div className="bg-white rounded p-2 space-y-1">
                        <div className="text-xs">
                          <span className="text-blue-600 font-medium">{day.emails_sent}</span>
                          <span className="text-gray-400 ml-1">sent</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-green-600 font-medium">{day.emails_opened}</span>
                          <span className="text-gray-400 ml-1">opened</span>
                        </div>
                        {day.spam_complaints > 0 && (
                          <div className="text-xs">
                            <span className="text-red-600 font-medium">{day.spam_complaints}</span>
                            <span className="text-gray-400 ml-1">spam</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Last Activity */}
            {accountMetrics.last_activity && (
              <div className="text-sm text-gray-600">
                Last activity: {new Date(accountMetrics.last_activity).toLocaleString()}
              </div>
            )}
          </div>
        ) : selectedAccount ? (
          <div className="text-center text-gray-500 py-8">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>No metrics available for this account</p>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p>Select an email account to view tracking metrics</p>
          </div>
        )}
      </div>

      {/* Domain Warmup Status Visualization */}
      <div className="bg-white rounded-lg p-4 sm:p-6 card-shadow">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Domain Warmup Status</h3>
            <p className="text-sm text-gray-600">Track which domains are ready for production use</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mt-3 sm:mt-0">
            {/* Domain Filter Dropdown */}
            <select
              value={selectedDomainFilter}
              onChange={(e) => {
                const domain = e.target.value;
                setSelectedDomainFilter(domain);
                // Auto-fetch when domain is selected
                if (domain !== 'all') {
                  fetchDomainHealth(domain);
                }
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Domain to Analyze</option>
              <option value="all">All Domains</option>
              {getUniqueDomains().map((domain) => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
            <button
              onClick={() => fetchDomainHealth(selectedDomainFilter === 'all' ? null : selectedDomainFilter)}
              disabled={loadingDomainHealth}
              className="btn-secondary text-sm px-4 py-2"
            >
              {loadingDomainHealth ? 'Analyzing...' : '🔄 Refresh Analysis'}
            </button>
          </div>
        </div>

        {loadingDomainHealth ? (
          <div className="flex items-center justify-center h-32">
            <div className="loading-spinner mr-2"></div>
            <span>Analyzing domain warmup status...</span>
          </div>
        ) : domainHealth.length > 0 ? (
          <div className="space-y-4">
            {/* Filter Results Info */}
            {selectedDomainFilter && selectedDomainFilter !== 'all' && selectedDomainFilter !== '' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Showing analysis for: <strong>{selectedDomainFilter}</strong> 
                  <button 
                    onClick={() => {
                      setSelectedDomainFilter('all');
                      fetchDomainHealth(null);
                    }}
                    className="ml-3 text-blue-600 hover:text-blue-800 underline text-xs"
                  >
                    Analyze all domains instead
                  </button>
                </p>
              </div>
            )}
            
            {selectedDomainFilter === 'all' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  Showing all domains • {domainHealth.length} domain{domainHealth.length !== 1 ? 's' : ''} analyzed
                </p>
              </div>
            )}
            
            {/* Domain Health Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {domainHealth
                .filter(d => !selectedDomainFilter || selectedDomainFilter === '' || selectedDomainFilter === 'all' || d.domain === selectedDomainFilter)
                .map((domain) => {
                const colorClasses = {
                  green: 'bg-green-50 border-green-300',
                  yellow: 'bg-yellow-50 border-yellow-300',
                  blue: 'bg-blue-50 border-blue-300',
                  gray: 'bg-gray-50 border-gray-300'
                };
                
                const progressBarColors = {
                  green: 'bg-green-500',
                  yellow: 'bg-yellow-500',
                  blue: 'bg-blue-500',
                  gray: 'bg-gray-400'
                };

                return (
                  <div key={domain.domain} className={`rounded-lg border-2 p-4 ${colorClasses[domain.statusColor]}`}>
                    {/* Domain Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="text-lg font-bold text-gray-900">{domain.domain}</div>
                        <span className="text-xs text-gray-600">({domain.accountCount} accounts)</span>
                      </div>
                      <div className="text-sm font-semibold">{domain.statusText}</div>
                    </div>

                    {/* Warmup Progress Bar */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">Warmup Score</span>
                        <span className="text-sm font-bold text-gray-900">{domain.warmupScore}/100</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all duration-500 ${progressBarColors[domain.statusColor]}`}
                          style={{ width: `${domain.warmupScore}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-4 gap-3 mb-3">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Successful Emails</div>
                        <div className="text-lg font-bold text-gray-900">{domain.successfulEmails}</div>
                        <div className="text-xs text-gray-500">
                          of {domain.totalEmails} sent
                        </div>
                        <div className="text-xs font-semibold text-indigo-600 mt-1">
                          Target: {domain.targetSuccessfulEmails}
                        </div>
                        <div className="text-xs text-gray-400">
                          ({domain.accountCount} accounts × 300)
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Delivery</div>
                        <div className="text-lg font-bold text-green-600">{domain.deliveryRate}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Spam</div>
                        <div className="text-lg font-bold text-red-600">{domain.spamRate}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Replies</div>
                        <div className="text-lg font-bold text-blue-600">{domain.replyRate}%</div>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="bg-white bg-opacity-60 rounded-lg p-2 mt-2">
                      <div className="flex items-start">
                        <svg className="w-4 h-4 text-gray-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-gray-700">{domain.recommendation}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Domain Health Legend */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Warmup Status Guide</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <div className="font-medium text-gray-900">Ready (80-100)</div>
                    <div className="text-gray-600">Production-ready domain</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div>
                    <div className="font-medium text-gray-900">Warming (50-79)</div>
                    <div className="text-gray-600">Continue gradual warmup</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <div>
                    <div className="font-medium text-gray-900">Early (20-49)</div>
                    <div className="text-gray-600">Build reputation slowly</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <div>
                    <div className="font-medium text-gray-900">New (0-19)</div>
                    <div className="text-gray-600">Just started warmup</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Warmup Score Calculation Info */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">How Warmup Score is Calculated</h4>
                  <div className="mt-2 text-xs text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Email Volume (40%):</strong> More emails sent = higher score (max 100 emails = full points)</li>
                      <li><strong>Delivery Rate (30%):</strong> Percentage of emails successfully delivered</li>
                      <li><strong>Spam Prevention (20%):</strong> Lower spam complaints = higher score</li>
                      <li><strong>Engagement (10%):</strong> Reply rate shows recipient engagement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Analyze Domain Warmup</h3>
            <p className="text-gray-600 mb-4">Choose how to view domain warmup status:</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-xs">
                <div className="text-sm font-medium text-blue-900 mb-1">Option 1: Analyze Specific Domain</div>
                <p className="text-xs text-blue-700">Select a domain from the dropdown above</p>
              </div>
              <div className="text-gray-400 font-bold">OR</div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-xs">
                <div className="text-sm font-medium text-green-900 mb-1">Option 2: Analyze All Domains</div>
                <p className="text-xs text-green-700">Select "All Domains" and click "Refresh Analysis"</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
